import { clsx } from "clsx";
import React, { useCallback, useState } from "react";
import AppFooter from "web/react/components/app-footer/app-footer";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { Divider } from "web/react/components/divider";
import { FooterProps } from "web/react/components/footer/footer";
import ProductCard from "web/react/components/product-card/react";
import { ProductDetails } from "web/react/components/product-card/react/emo";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import { InfoCallout } from "web/react/emo/info-callout";
import { ProductImage } from "web/react/emo/product-card";
import {
    EventLabel,
    PinSizeInfo,
    useSaveForLater,
} from "web/react/hooks/use-save-for-later/use-save-for-later";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import { ProductCardFactory } from "web/types/examples/product-card";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
    ProductScreenSize,
} from "web/types/serializers";
import * as styles from "./out-of-stock-product-page.css";

const getInfoCalloutTitle = (
    buyOptions: ProductBuyOptionsSerializer[],
    uniqueRetailerCount: number,
    isCheckout: boolean
): string => {
    if (!buyOptions?.length) return gettext("oos_product.info_callout_no_retailers");
    else if (isCheckout) return gettext("oos_product.info_callout_icon");
    else {
        return uniqueRetailerCount === 2
            ? gettext("oos_product.info_callout_affiliate_two_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
              })
            : uniqueRetailerCount > 2
            ? gettext("oos_product.info_callout_affiliate_multiple_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
                  retailer_count: uniqueRetailerCount - 1,
              })
            : gettext("oos_product.info_callout_affiliate", {
                  retailer_name: buyOptions[0].retailer_name,
              });
    }
};

// TODO BEAM-1120 remove
const product = ProductCardFactory();

// remove me as the page is built
function TempSection({ title, id }: { title: string; id?: string }): React.ReactElement {
    return (
        <div className={styles.tempSectionContainer} id={id}>
            {title}
        </div>
    );
}

function SectionDivider({ isMobileOnly }: { isMobileOnly?: boolean }): React.ReactElement {
    return (
        <div className={clsx({ [styles.hideDesktop]: isMobileOnly })}>
            <Divider />
        </div>
    );
}

export interface OutOfStockProductPageProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    isCheckout: boolean;
    isOverlay?: boolean;
    parentRef?: React.RefObject<HTMLElement>;
    footerData?: FooterProps;
}
export function OutOfStockProductPage({
    productArea,
    // productLayout,
    isOverlay,
    isCheckout,
    // parentRef,
    footerData,
}: OutOfStockProductPageProps): React.ReactElement {
    const [selectedSize, setSelectedSize] = useState<ProductScreenSize | undefined>(undefined);

    const [isSizePickerExpanded, setSizePickerExpanded] = useState<boolean>(false);

    const sizes = productArea.sizes.all_sizes;

    const schemas = productArea.sizes.schema_labels;

    const userDefaultSchema = productArea.sizes.user_default_schema;

    const isUserLoggedIn = userProfiler.isLoggedIn();

    const uniqueRetailerCount = productArea.unique_retailers.length;

    const { isSaved: productIsSaved, toggleSaveForLater } = useSaveForLater(
        productArea.product.product_id,
        EventLabel.OOS,
        undefined,
        undefined
    );

    const convertSizeToPinInfo = useCallback((size: ProductScreenSize): PinSizeInfo[] | null => {
        if (!size.localized_sizes || !size.user_default_schema) {
            return null;
        }
        const localizedSize = size.localized_sizes[size.user_default_schema];

        if (!localizedSize || !localizedSize.tag) {
            return null;
        }

        // The tag might be a JSON string like:
        // '{"first_dimension": "size.jeans.au.w.16", "second_dimension": "size.jeans.inseam.inch.30"}'
        const sizeTag = localizedSize.tag;
        let pinSizes: PinSizeInfo[] = [];
        if (sizeTag.startsWith("{")) {
            try {
                const parsedTag = JSON.parse(sizeTag);
                pinSizes = Object.values(parsedTag).map((dimensionTag: any) => ({
                    size_tag: dimensionTag,
                }));
            } catch (e) {
                console.warn(
                    `Failed to parse multi-dimensional size tag: ${sizeTag}, threw error ${e}`
                );
            }
        } else {
            pinSizes.push({ size_tag: sizeTag }); // tag: "size.dress.uk.10"
        }
        return pinSizes;
    }, []);

    const productIsSavedButtonText =
        uniqueRetailerCount > 1
            ? gettext("oos_product.tracking_mutiple_retailers_notification", {
                  retailer_count: uniqueRetailerCount,
              })
            : gettext("oos_product.tracking_single_retailer_notification");

    const hasSizes = sizes.length > 0 && sizes.every((size) => size.localized_sizes);

    const handleSizePickerSelect = useCallback(
        (option: any) => {
            const size = option.value as ProductScreenSize;
            const sizeInfo = convertSizeToPinInfo(size);
            setSelectedSize(size);
            setSizePickerExpanded(false);

            if (sizeInfo) {
                toggleSaveForLater(sizeInfo);
            }
        },
        [convertSizeToPinInfo, toggleSaveForLater]
    );

    return (
        <>
            <div className={styles.pageWrapper}>
                {!isOverlay && <CustomerCaptureOverlay />}
                <div
                    className={clsx(
                        styles.pageContent,
                        styles.pageContentPadding[isOverlay ? "overlay" : "standard"]
                    )}
                >
                    <div className={styles.heroSection}>
                        <InfoCallout
                            variant="information"
                            title={getInfoCalloutTitle(
                                productArea?.buy_options,
                                uniqueRetailerCount,
                                isCheckout
                            )}
                        />
                        <TempSection title={"last seen"} />
                        <div className={styles.mainBuyOptionPlusOtherOptionsContainer}>
                            <div style={{ width: "300px" }}>
                                <ProductCard product={product}>
                                    <ProductImage />
                                    <ProductDetails />
                                </ProductCard>
                            </div>
                            {/* TODO remove div - just used for testing purposes */}
                            <div style={{ width: "300px" }}>
                                {uniqueRetailerCount > 0 && (
                                    <SaveForLaterButton
                                        type="cta"
                                        cta={{
                                            title: gettext(
                                                "oos_product.get_notified_when_back_in_stock_btn"
                                            ),
                                        }}
                                        initialIsSaved={productIsSaved}
                                        activeCta={{
                                            title: productIsSavedButtonText,
                                            icon: "notification-active",
                                            iconPosition: "right",
                                        }}
                                        {...(isUserLoggedIn && { onClick: toggleSaveForLater })}
                                        pageSource="oos"
                                        productId={productArea.product.product_id}
                                        isLoggedIn={isUserLoggedIn}
                                        size="lg"
                                        variant="primary"
                                        className={styles.fullWidthButton}
                                    />
                                )}
                                {hasSizes && productIsSaved && (
                                    <div style={{ marginTop: "5px" }}>
                                        <SizePicker
                                            onSelect={handleSizePickerSelect}
                                            isExpanded={isSizePickerExpanded}
                                            schemas={schemas}
                                            defaultSelectText={gettext(
                                                "oos_product.select_size_for_personalized_alerts"
                                            )}
                                            userDefaultSchema={userDefaultSchema}
                                            setExpanded={(expanded: boolean): void =>
                                                setSizePickerExpanded(expanded)
                                            }
                                            sizes={sizes}
                                            productType={productArea.product_purchase_type}
                                            selectedOption={selectedSize}
                                            isExpressCheckout={false}
                                            isComparison={false}
                                            showDisabledOptions={true}
                                            showAllSchemas={true}
                                            selectedTextPrefix={
                                                selectedSize
                                                    ? ` ${gettext(
                                                          "oos_product.alert_set_for_size"
                                                      )} `
                                                    : undefined
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <TempSection title={"previously available"} />
                        </div>
                    </div>
                    <SectionDivider />
                    <TempSection title={"pba"} />
                    <TempSection title={"more like this grid"} />
                    <TempSection title={"similar brands to"} />
                    <SectionDivider isMobileOnly />
                    <TempSection title={"full product details"} id="product-details" />
                    <TempSection title={"people also searched for"} />
                    <SectionDivider isMobileOnly />
                    <TempSection title={"recently viewed"} />
                </div>
            </div>
            {footerData && (
                <AppFooter
                    data={footerData.data}
                    helpCentreURLs={footerData.helpCentreURLs}
                    country={footerData.country}
                />
            )}
        </>
    );
}
